import { createSlice } from '@reduxjs/toolkit';
import { apiCallBegan } from './middleware/apiMiddleware';

/**
 * VENUE SLICE - Venue Management State
 * Pattern: Follows middleware-based workflow with centralized API calls
 */
const initialState = {
    // Venues list
    venues: [],

    // Soft deleted venues (local only - for UI demo)
    softDeletedVenues: [],

    // Create Venue operation states
    createVenueLoading: false,
    createVenueError: null,
    createdVenue: null,
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'

    // Fetch Venues operation states
    fetchVenuesLoading: false,
    fetchVenuesError: null,

    // Delete Venue operation states
    deleteVenueLoading: false,
    deleteVenueError: null,

    // Update Venue operation states
    updateVenueLoading: false,
    updateVenueError: null,
    updateVenueStatus: 'idle',

    // [ADDED] Public Venues (all venues) - Used for fallback when user list lacks details
    publicVenues: [],
    fetchPublicVenuesLoading: false,
    fetchPublicVenuesError: null,

    // Fetch Single Venue operation states
    currentVenue: null,
    fetchVenueByIdLoading: false,
    fetchVenueByIdError: null,
};

const venueSlice = createSlice({
    name: 'venue',
    initialState,
    reducers: {
        // ============ CREATE VENUE REDUCERS ============
        beginCreateVenueLoader: (state) => {
            state.createVenueLoading = true;
            state.createVenueError = null;
            state.status = 'loading';
        },
        setCreateVenueData: (state, action) => {
            state.createVenueLoading = false;
            // Unwrap venue from API response wrapper {status, message, data}
            const payload = action.payload;
            const venueData = payload?.data || payload;

            console.log('Create Venue Response:', payload);
            console.log('Extracted venue data:', venueData);

            // Don't add to venues list here - the Venues page will fetch fresh data
            // This prevents inconsistency between create response and fetch response structures
            state.createdVenue = venueData;
            state.createVenueError = null;
            state.status = 'succeeded';
        },
        createVenueError: (state, action) => {
            state.createVenueLoading = false;
            state.createVenueError = action.payload?.errorMessage || 'Failed to create venue';
            state.status = 'failed';
        },

        // ============ FETCH VENUES REDUCERS ============
        beginFetchVenuesLoader: (state) => {
            state.fetchVenuesLoading = true;
            state.fetchVenuesError = null;
        },
        setFetchVenuesData: (state, action) => {
            state.fetchVenuesLoading = false;
            // Handle different API response structures
            const payload = action.payload;
            console.log('Fetch Venues API Response:', payload);

            if (Array.isArray(payload)) {
                // Response is an array - check if items are wrapped
                if (payload.length > 0 && payload[0]?.data) {
                    // Each item has a .data wrapper: [{status, message, data}, ...]
                    state.venues = payload.map(item => item.data);
                    console.log('Unwrapped venues:', state.venues);
                } else {
                    // Response is directly an array of venues
                    state.venues = payload;
                }
            } else if (payload?.data && Array.isArray(payload.data)) {
                // Response has data wrapper: { data: [...] }
                state.venues = payload.data;
            } else if (payload?.data && typeof payload.data === 'object') {
                // Single venue with data wrapper: { status, message, data: {...venue} }
                state.venues = [payload.data];
            } else if (payload?.venues && Array.isArray(payload.venues)) {
                // Response has venues key: { venues: [...] }
                state.venues = payload.venues;
            } else if (payload?.results && Array.isArray(payload.results)) {
                // Response has results key: { results: [...] }
                state.venues = payload.results;
            } else {
                // Fallback - might be a single venue or unexpected format
                console.warn('Unexpected venues response format:', payload);
                state.venues = payload ? [payload] : [];
            }
            state.fetchVenuesError = null;
        },
        fetchVenuesError: (state, action) => {
            state.fetchVenuesLoading = false;
            state.fetchVenuesError = action.payload?.errorMessage || 'Failed to fetch venues';
        },

        // ============ FETCH PUBLIC VENUES REDUCERS ============
        beginFetchPublicVenuesLoader: (state) => {
            state.fetchPublicVenuesLoading = true;
            state.fetchPublicVenuesError = null;
        },
        setFetchPublicVenuesData: (state, action) => {
            state.fetchPublicVenuesLoading = false;
            const payload = action.payload;

            // Logic similar to setFetchVenuesData but for publicVenues
            if (Array.isArray(payload)) {
                if (payload.length > 0 && payload[0]?.data) {
                    state.publicVenues = payload.map(item => item.data);
                } else {
                    state.publicVenues = payload;
                }
            } else if (payload?.data && Array.isArray(payload.data)) {
                state.publicVenues = payload.data;
            } else if (payload?.results && Array.isArray(payload.results)) {
                state.publicVenues = payload.results;
            } else {
                state.publicVenues = payload ? [payload] : [];
            }
            state.fetchPublicVenuesError = null;
        },
        fetchPublicVenuesError: (state, action) => {
            state.fetchPublicVenuesLoading = false;
            state.fetchPublicVenuesError = action.payload?.errorMessage || 'Failed to fetch public venues';
        },

        // ============ DELETE VENUE REDUCERS ============
        beginDeleteVenueLoader: (state) => {
            state.deleteVenueLoading = true;
            state.deleteVenueError = null;
        },
        setDeleteVenueData: (state, action) => {
            state.deleteVenueLoading = false;
            // Get venueId from extraData or response
            const payload = action.payload;
            console.log('Delete Venue Response:', payload);

            // Try to get venueId from different possible locations
            const deletedVenueId = payload?.extraData?.venueId ||
                payload?.venueId ||
                payload?.data?.id ||
                payload?.id ||
                payload;

            console.log('Deleting venue with ID:', deletedVenueId);
            state.venues = state.venues.filter(venue => venue.id !== deletedVenueId);
            state.deleteVenueError = null;
        },
        deleteVenueError: (state, action) => {
            state.deleteVenueLoading = false;
            state.deleteVenueError = action.payload?.errorMessage || 'Failed to delete venue';
        },

        // ============ UPDATE VENUE REDUCERS ============
        beginUpdateVenueLoader: (state) => {
            state.updateVenueLoading = true;
            state.updateVenueError = null;
            state.updateVenueStatus = 'loading';
        },
        setUpdateVenueData: (state, action) => {
            state.updateVenueLoading = false;
            const payload = action.payload;
            const venueData = payload?.data || payload;

            console.log('Update Venue Response:', payload);
            console.log('Updated venue data:', venueData);

            // Update venue in the list if it exists
            const index = state.venues.findIndex(v => v.id === venueData.id);
            if (index !== -1) {
                state.venues[index] = venueData;
            }
            state.updateVenueError = null;
            state.updateVenueStatus = 'succeeded';
        },
        updateVenueError: (state, action) => {
            state.updateVenueLoading = false;
            state.updateVenueError = action.payload?.errorMessage || 'Failed to update venue';
            state.updateVenueStatus = 'failed';
        },

        // ============ FETCH VENUE BY ID REDUCERS ============
        beginFetchVenueByIdLoader: (state) => {
            state.fetchVenueByIdLoading = true;
            state.fetchVenueByIdError = null;
        },
        setFetchVenueByIdData: (state, action) => {
            state.fetchVenueByIdLoading = false;
            const payload = action.payload;
            const venueData = payload?.data || payload;

            console.log('Fetch Venue By ID Response:', payload);
            state.currentVenue = venueData;
            state.fetchVenueByIdError = null;
        },
        fetchVenueByIdError: (state, action) => {
            state.fetchVenueByIdLoading = false;
            state.fetchVenueByIdError = action.payload?.errorMessage || 'Failed to fetch venue';
        },

        // ============ SOFT DELETE REDUCERS (Local only) ============
        softDeleteVenue: (state, action) => {
            const venueId = action.payload;
            const venueToDelete = state.venues.find(v => v.id === venueId);
            if (venueToDelete) {
                // Move to soft deleted list
                state.softDeletedVenues.push({ ...venueToDelete, deletedAt: new Date().toISOString() });
                // Remove from active venues
                state.venues = state.venues.filter(v => v.id !== venueId);
                console.log('Soft deleted venue:', venueId);
            }
        },
        restoreVenue: (state, action) => {
            const venueId = action.payload;
            const venueToRestore = state.softDeletedVenues.find(v => v.id === venueId);
            if (venueToRestore) {
                // Remove deletedAt and move back to active venues
                const { deletedAt, ...restoredVenue } = venueToRestore;
                state.venues.push(restoredVenue);
                // Remove from soft deleted list
                state.softDeletedVenues = state.softDeletedVenues.filter(v => v.id !== venueId);
                console.log('Restored venue:', venueId);
            }
        },
        permanentlyDeleteVenue: (state, action) => {
            const venueId = action.payload;
            state.softDeletedVenues = state.softDeletedVenues.filter(v => v.id !== venueId);
            console.log('Permanently deleted venue:', venueId);
        },

        // ============ RESET REDUCERS ============
        clearCreateVenueLoader: (state) => {
            state.createVenueLoading = false;
        },
        clearCreateVenueError: (state) => {
            state.createVenueError = null;
        },
        resetStatus: (state) => {
            state.status = 'idle';
        },
        clearUpdateVenueLoader: (state) => {
            state.updateVenueLoading = false;
        },
        clearUpdateVenueError: (state) => {
            state.updateVenueError = null;
        },
        resetUpdateVenueStatus: (state) => {
            state.updateVenueStatus = 'idle';
        },
        clearCurrentVenue: (state) => {
            state.currentVenue = null;
            state.fetchVenueByIdError = null;
        },
    },
});

// ============ EXPORT ACTIONS ============
export const {
    beginCreateVenueLoader,
    setCreateVenueData,
    createVenueError,
    beginFetchVenuesLoader,
    setFetchVenuesData,
    fetchVenuesError,
    beginFetchPublicVenuesLoader,
    setFetchPublicVenuesData,
    fetchPublicVenuesError,    
    beginDeleteVenueLoader,
    setDeleteVenueData,
    deleteVenueError,
    beginUpdateVenueLoader,
    setUpdateVenueData,
    updateVenueError,
    beginFetchVenueByIdLoader,
    setFetchVenueByIdData,
    fetchVenueByIdError,
    softDeleteVenue,
    restoreVenue,
    permanentlyDeleteVenue,
    clearCreateVenueLoader,
    clearCreateVenueError,
    resetStatus,
    clearUpdateVenueLoader,
    clearUpdateVenueError,
    resetUpdateVenueStatus,
    clearCurrentVenue,
} = venueSlice.actions;

// ============ THUNK ACTIONS ============

/**
 * CREATE VENUE ACTION
 * POST /venues
 */
export const createVenue = (venueData) => (dispatch) => {
    const url = '/venues';
    const method = 'POST';

    dispatch(
        apiCallBegan({
            url,
            method,
            data: venueData,
            onStart: beginCreateVenueLoader.type,
            onSuccess: setCreateVenueData.type,
            onError: createVenueError.type,
            showGlobalError: true,
        })
    );
};

/**
 * FETCH ALL VENUES ACTION
 * GET /my-venues
 */
export const fetchVenues = () => (dispatch) => {
    // REPLACED API: Changed endpoint from '/venues' to '/my-venues' to fetch only logged-in user's venues
    const url = '/my-venues';
    const method = 'GET';

    dispatch(
        apiCallBegan({
            url,
            method,
            onStart: beginFetchVenuesLoader.type,
            onSuccess: setFetchVenuesData.type,
            onError: fetchVenuesError.type,
            showGlobalError: true,
        })
    );
};

/**
 * [ADDED] FETCH PUBLIC VENUES ACTION
 * GET /venues
 * Fetches the public list of venues which appears to contain image data
 */
export const fetchPublicVenues = () => (dispatch) => {
    const url = '/venues';
    const method = 'GET';

    dispatch(
        apiCallBegan({
            url,
            method,
            onStart: beginFetchPublicVenuesLoader.type,
            onSuccess: setFetchPublicVenuesData.type,
            onError: fetchPublicVenuesError.type,
            showGlobalError: true,
        })
    );
};

/**
 * DELETE VENUE ACTION
 * DELETE /venues/{venue_id}
 */
export const deleteVenue = (venueId) => (dispatch) => {
    const url = `/venues/${venueId}`;
    const method = 'DELETE';

    dispatch(
        apiCallBegan({
            url,
            method,
            onStart: beginDeleteVenueLoader.type,
            onSuccess: setDeleteVenueData.type,
            onError: deleteVenueError.type,
            showGlobalError: true,
            // Pass venueId via meta so it's included in the payload
            meta: { venueId },
        })
    );
};

/**
 * UPDATE VENUE ACTION
 * PUT /venues/{venue_id}
 */
export const updateVenue = (venueId, venueData) => (dispatch) => {
    const url = `/venues/${venueId}`;
    const method = 'PUT';

    dispatch(
        apiCallBegan({
            url,
            method,
            data: venueData,
            onStart: beginUpdateVenueLoader.type,
            onSuccess: setUpdateVenueData.type,
            onError: updateVenueError.type,
            showGlobalError: true,
        })
    );
};

/**
 * FETCH VENUE BY ID ACTION
 * GET /venues/{venue_id}
 */
export const fetchVenueById = (venueId) => (dispatch) => {
    const url = `/venues/${venueId}`;
    const method = 'GET';

    dispatch(
        apiCallBegan({
            url,
            method,
            onStart: beginFetchVenueByIdLoader.type,
            onSuccess: setFetchVenueByIdData.type,
            onError: fetchVenueByIdError.type,
            showGlobalError: true,
        })
    );
};

// ============ SELECTORS ============
export const selectCreateVenueLoading = (state) => state.venue.createVenueLoading;
export const selectCreateVenueError = (state) => state.venue.createVenueError;
export const selectCreatedVenue = (state) => state.venue.createdVenue;
export const selectVenues = (state) => state.venue.venues;
export const selectFetchVenuesLoading = (state) => state.venue.fetchVenuesLoading;
export const selectPublicVenues = (state) => state.venue.publicVenues;
export const selectFetchPublicVenuesLoading = (state) => state.venue.fetchPublicVenuesLoading;
export const selectVenueStatus = (state) => state.venue.status;
export const selectDeleteVenueLoading = (state) => state.venue.deleteVenueLoading;
export const selectDeleteVenueError = (state) => state.venue.deleteVenueError;
export const selectUpdateVenueLoading = (state) => state.venue.updateVenueLoading;
export const selectUpdateVenueError = (state) => state.venue.updateVenueError;
export const selectUpdateVenueStatus = (state) => state.venue.updateVenueStatus;
export const selectCurrentVenue = (state) => state.venue.currentVenue;
export const selectFetchVenueByIdLoading = (state) => state.venue.fetchVenueByIdLoading;
export const selectFetchVenueByIdError = (state) => state.venue.fetchVenueByIdError;
export const selectSoftDeletedVenues = (state) => state.venue.softDeletedVenues;

export default venueSlice.reducer;

